import { cn } from '@/lib/utils.js';
import { useFavoriteStore } from '@/stores/favoriteStore';
import { ProductListItem } from '@/types.js';
import { Heart } from 'lucide-react';
import { memo } from 'react';
import { useShallow } from 'zustand/react/shallow';

interface FavoriteButtonProps {
    productVariation: ProductListItem;
    className?: string;
    variant?: 'ghost' | 'box';
}

function FavoriteButtonComponent({
    productVariation,
    className,
    variant = 'ghost',
}: FavoriteButtonProps) {
    const isFavorite = useFavoriteStore(
        useShallow((state) => state.isFavorite(productVariation.id)),
    );
    const isProcessing = useFavoriteStore(
        useShallow((state) => state.isProcessing(productVariation.id)),
    );
    const toggleFavorite = useFavoriteStore((state) => state.toggleFavorite);

    const active = !!isFavorite;

    const handleToggleFavorite = (): void => {
        toggleFavorite(productVariation);
    };

    return (
        <button
            disabled={isProcessing}
            onClick={handleToggleFavorite}
            className={cn(
                'flex items-center justify-center transition-all duration-300',
                // Ghost variant (for cards/images)
                variant === 'ghost' && [
                    'rounded-full border border-zinc-800/50 bg-zinc-950/60 p-2 shadow-lg backdrop-blur-md hover:scale-110',
                    active
                        ? 'border-red-500/30 text-red-500 drop-shadow-[0_0_8px_rgba(239,68,68,0.5)]'
                        : 'text-zinc-400 hover:border-red-400/50 hover:text-red-400',
                ],
                // Box variant (for action bars)
                variant === 'box' && [
                    'h-12 w-12 shrink-0 rounded-md border active:scale-90',
                    active
                        ? 'border-red-500 bg-red-500/10 text-red-500 shadow-[0_0_15px_rgba(239,68,68,0.1)]'
                        : 'border-zinc-800 bg-zinc-950 text-zinc-500 hover:border-red-500/50 hover:text-red-400',
                ],
                className,
            )}
            aria-label={active ? 'Remove from favorites' : 'Add to favorites'}
        >
            <Heart
                className={cn(
                    'h-5 w-5 transition-transform duration-300',
                    active ? 'fill-current' : '',
                )}
            />
        </button>
    );
}
export const FavoriteButton = memo(
    FavoriteButtonComponent,
    (prevProps, nextProps) => {
        return prevProps.productVariation.id === nextProps.productVariation.id;
    },
);
